// OTP Service for email verification during registration
// This service calls the backend API to generate and verify OTPs securely

/**
 * Send OTP to the specified email address
 * @param {string} email - User's email address
 * @returns {Promise<{success: boolean, hash?: string, expiry?: number, error?: string}>}
 */
export const sendOtp = async (email) => {
    try {
        const response = await fetch('/api/otp-service', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                action: 'send_otp',
                email: email
            }),
        });

        const result = await response.json();

        if (!response.ok) {
            // Log detailed error for debugging
            console.error('OTP Service Error:', {
                status: response.status,
                error: result.error,
                details: result.details,
                code: result.code,
                serverResponse: result.serverResponse
            });
            throw new Error(result.details || result.error || 'Failed to send OTP');
        }

        return {
            success: true,
            hash: result.hash,
            expiry: result.expiry
        };
    } catch (error) {
        console.error('Error sending OTP:', error);
        return {
            success: false,
            error: error.message || 'Failed to send OTP'
        };
    }
};

/**
 * Verify the OTP entered by the user
 * @param {string} email - User's email address
 * @param {string} otp - OTP entered by user
 * @param {string} hash - Hash received from sendOtp
 * @param {number} expiry - Expiry timestamp received from sendOtp
 * @returns {Promise<{success: boolean, error?: string, code?: string}>}
 */
export const verifyOtp = async (email, otp, hash, expiry) => {
    try {
        const response = await fetch('/api/otp-service', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                action: 'verify_otp',
                email: email,
                otp: otp,
                hash: hash,
                expiry: expiry
            }),
        });

        const result = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: result.error || 'Invalid OTP',
                code: result.code
            };
        }

        return {
            success: true
        };
    } catch (error) {
        console.error('Error verifying OTP:', error);
        return {
            success: false,
            error: error.message || 'Failed to verify OTP'
        };
    }
};

export const otpService = {
    sendOtp,
    verifyOtp
};
